/**
 * Dashboards API client (P3-S2 Doc 42).
 */
import { api } from "./client";

export type WidgetType = "kpi" | "bar" | "line" | "donut" | "table";

export interface GridPos {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WidgetSpec {
  id: string;
  type: WidgetType;
  title: string;
  description?: string | null;
  grid: GridPos;
  data_source: string;
  data_params: Record<string, unknown>;
  options: Record<string, unknown>;
}

export interface DashboardSpec {
  code: string;
  title: string;
  description?: string | null;
  widgets: WidgetSpec[];
  metadata: Record<string, unknown>;
}

export interface DashboardOut {
  id: string;
  code: string;
  title: string;
  description: string | null;
  spec: DashboardSpec;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardSummary {
  id: string;
  code: string;
  title: string;
  description: string | null;
  widget_count: number;
  is_archived: boolean;
  updated_at: string;
}

export interface DataSourceInfo {
  name: string;
  description: string;
  output_shape: string;
  params_schema: Record<string, unknown>;
}

export interface GenerateResponse {
  ok: boolean;
  spec?: DashboardSpec | null;
  explanation?: string;
  warnings?: string[];
  error?: string | null;
  raw_text?: string | null;
}

export interface ResolveResponse {
  ok: boolean;
  data?: Record<string, unknown> | null;
  error?: string | null;
}

export const dashboardsApi = {
  listSources: () =>
    api.get<DataSourceInfo[]>("/api/v1/admin/dashboards/data-sources"),

  list: (includeArchived = false) =>
    api.get<{ items: DashboardSummary[] }>("/api/v1/admin/dashboards", {
      query: { include_archived: includeArchived },
    }),

  get: (code: string) =>
    api.get<DashboardOut>(`/api/v1/admin/dashboards/${encodeURIComponent(code)}`),

  create: (body: {
    code: string;
    title: string;
    description?: string | null;
    spec: DashboardSpec;
  }) => api.post<DashboardOut>("/api/v1/admin/dashboards", body),

  update: (
    code: string,
    body: {
      title?: string | null;
      description?: string | null;
      spec?: DashboardSpec | null;
    },
  ) =>
    api.patch<DashboardOut>(
      `/api/v1/admin/dashboards/${encodeURIComponent(code)}`,
      body,
    ),

  archive: (code: string) =>
    api.delete<void>(`/api/v1/admin/dashboards/${encodeURIComponent(code)}`),

  generate: (intent: string, llmProvider?: string, llmModel?: string) =>
    api.post<GenerateResponse>("/api/v1/admin/dashboards/_generate", {
      intent,
      llm_provider: llmProvider,
      llm_model: llmModel,
    }),

  resolveWidget: (
    dataSource: string,
    dataParams: Record<string, unknown>,
    useCache = true,
  ) =>
    api.post<ResolveResponse>("/api/v1/admin/dashboards/_resolve-widget", {
      data_source: dataSource,
      data_params: dataParams,
      use_cache: useCache,
    }),
};
